import { Dialog } from '@headlessui/react';
import { ChevronLeft, ChevronRight, Info, Maximize2, Minimize2, Pause, Play, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDrillPlayback } from '../../drilleditor/hooks/useDrillPlayback';
import type { Actor, Drill, Step, ExercisePitch } from '../../drilleditor/types';
import { useI18n, type Locale } from '../../i18n';
import ExerciseFieldPreview from './ExerciseFieldPreview';

type ExercisePlaybackProps = {
  id: string;
  title: string;
  actors: Actor[];
  timeline: Step[];
  pitch?: ExercisePitch;
  variant?: 'indoor' | 'outdoor';
  compact?: boolean;
  contentLocale?: Locale;
  animationNoteId?: string;
};

const compactSecondaryButton = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 enabled:hover:bg-gray-100 disabled:opacity-35 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-700';
const compactUtilityAction = 'inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-950 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-700';

const stepStartTime = (timeline: Step[], index: number): number =>
  timeline.slice(0, Math.max(0, index)).reduce((total, step) => total + Math.max(0, step.ms), 0);

const formatPlaybackTime = (milliseconds: number): string => {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

export default function ExercisePlayback({ id, title, actors, timeline, pitch, variant, compact = false, contentLocale, animationNoteId }: ExercisePlaybackProps) {
  const { t, locale } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const drill = useMemo<Drill>(() => ({ id, title, actors, timeline }), [actors, id, timeline, title]);
  const { isPreviewPlaying, previewState, play, stop, reset, applyPreviewAtTime } = useDrillPlayback(drill);
  const totalDuration = timeline.reduce((total, step) => total + Math.max(0, step.ms), 0);
  const hasPlayback = actors.length > 0 && timeline.some(step => step.ms > 0 && step.parallel?.some(action => action.type !== 'delay')) && totalDuration > 0;
  const currentTime = previewState?.currentTime ?? 0;
  const currentStepIndex = Math.min(previewState?.currentStepIndex ?? 0, Math.max(0, timeline.length - 1));
  const currentStep = timeline[currentStepIndex];
  const atStart = currentTime <= 0;
  const atEnd = currentTime >= totalDuration;
  const stepLabel = currentStep?.labels?.[contentLocale ?? locale] || currentStep?.label || t('training_playback_step', { current: currentStepIndex + 1, total: timeline.length });
  const playbackTime = `${formatPlaybackTime(currentTime)} / ${formatPlaybackTime(totalDuration)}`;
  const playLabel = t(isPreviewPlaying ? 'training_playback_pause' : atEnd ? 'training_playback_replay' : 'training_playback_play');

  const seek = (time: number) => {
    stop();
    applyPreviewAtTime(Math.max(0, Math.min(time, totalDuration)));
  };

  const previous = () => {
    const currentStart = stepStartTime(timeline, currentStepIndex);
    seek(currentTime > currentStart + 100 ? currentStart : stepStartTime(timeline, currentStepIndex - 1));
  };

  const next = () => {
    seek(currentStepIndex < timeline.length - 1
      ? stepStartTime(timeline, currentStepIndex + 1)
      : totalDuration);
  };

  const progress = (
    <input
      type="range"
      min={0}
      max={Math.max(1, totalDuration)}
      step={50}
      value={Math.min(currentTime, Math.max(1, totalDuration))}
      onChange={event => seek(Number(event.target.value))}
      aria-label={t('training_playback_progress')}
      aria-valuetext={compact ? playbackTime : undefined}
      className={compact
        ? 'col-span-3 row-start-2 block h-8 w-full cursor-pointer rounded accent-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-700 sm:col-span-1 sm:col-start-2 sm:h-6'
        : 'h-2 w-full cursor-pointer accent-orange-600'}
    />
  );

  if (compact) {
    const isIndoor = (pitch?.variant ?? variant) === 'indoor';
    const renderField = (inDialog: boolean) => (
      <div className={`flex justify-center overflow-hidden rounded-md ${isIndoor ? 'bg-[#20516c]' : 'bg-[#2f6a35]'}`}>
        <ExerciseFieldPreview
          actors={actors}
          pitch={pitch}
          variant={variant}
          markerScale={1.55}
          playbackState={inDialog || !expanded ? previewState : null}
          style={{
            width: '100%',
            maxWidth: inDialog ? 'min(100%, 78dvh)' : 'min(100%, clamp(240px, min(50vh, 68vw), 425px))',
            height: 'auto',
            maxHeight: 'none',
            aspectRatio: '5 / 4',
          }}
        />
      </div>
    );
    const fieldActions = <>
      {animationNoteId && <a href={`#${animationNoteId}`} aria-label={t('exercise_viewer_animation_note')}
        title={t('exercise_viewer_animation_note')} className={compactSecondaryButton}>
        <Info size={17} aria-hidden="true" />
      </a>}
      {actors.length > 0 && <button type="button" onClick={() => setExpanded(true)} aria-haspopup="dialog"
        aria-label={t('training_field_expand')} title={t('training_field_expand')} className={compactSecondaryButton}>
        <Maximize2 size={17} aria-hidden="true" />
      </button>}
    </>;
    const renderControls = (inDialog: boolean) => hasPlayback && (
      <div className="mt-2 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3">
        <div className="col-span-3 row-start-1 flex min-w-0 items-center justify-between gap-2 text-xs sm:col-span-1 sm:col-start-2">
          <span lang={contentLocale} className="truncate font-semibold text-gray-800" title={stepLabel}>{stepLabel}</span>
          <span className="shrink-0 tabular-nums text-gray-600">{playbackTime}</span>
        </div>
        <button
          type="button"
          onClick={isPreviewPlaying ? stop : play}
          aria-label={playLabel}
          title={playLabel}
          className="col-start-1 row-start-3 flex h-11 w-11 items-center justify-center rounded-md bg-primary-700 text-white hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 sm:row-span-2 sm:row-start-1"
        >
          {isPreviewPlaying ? <Pause size={16} fill="currentColor" aria-hidden="true" /> : atEnd ? <RotateCcw size={18} aria-hidden="true" /> : <Play size={16} fill="currentColor" aria-hidden="true" />}
        </button>
        {progress}
        <div className="col-start-2 col-span-2 row-start-3 flex flex-wrap items-center justify-end max-sm:[&>*]:w-9 sm:col-start-3 sm:col-span-1 sm:row-span-2 sm:row-start-1">
          <button type="button" onClick={previous} disabled={atStart} aria-label={t('training_playback_previous')} title={t('training_playback_previous')} className={compactSecondaryButton}><ChevronLeft size={19} aria-hidden="true" /></button>
          <button type="button" onClick={next} disabled={atEnd} aria-label={t('training_playback_next')} title={t('training_playback_next')} className={compactSecondaryButton}><ChevronRight size={19} aria-hidden="true" /></button>
          <button type="button" onClick={reset} disabled={atStart} aria-label={t('training_playback_reset')} title={t('training_playback_reset')} className={compactSecondaryButton}><RotateCcw size={16} aria-hidden="true" /></button>
          {!inDialog && fieldActions}
        </div>
      </div>
    );

    return (
      <div className="min-w-0">
        {actors.length ? renderField(false) : <p className="py-8 text-sm text-gray-500">{t('training_field_empty')}</p>}
        {renderControls(false)}
        {!hasPlayback && <div className="flex justify-end">{fieldActions}</div>}
        <Dialog open={expanded} onClose={setExpanded} className="relative z-50">
          <div aria-hidden="true" className="fixed inset-0 bg-gray-950/60" />
          <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-2 sm:p-4">
            <Dialog.Panel className="max-h-[calc(100dvh-1rem)] w-full max-w-5xl overflow-y-auto rounded-lg bg-white p-3 sm:p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <Dialog.Title lang={contentLocale} className="min-w-0 break-words font-semibold text-gray-950">{title}</Dialog.Title>
                <button type="button" onClick={() => setExpanded(false)} className={`${compactUtilityAction} shrink-0`}>
                  <Minimize2 size={15} aria-hidden="true" />
                  {t('training_field_compact')}
                </button>
              </div>
              {renderField(true)}
              {renderControls(true)}
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="overflow-hidden border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-3 py-1">
        <span className="text-xs text-gray-600">{t(hasPlayback ? 'training_exercises_detail_field' : 'training_setup')}</span>
        <button type="button" onClick={() => setExpanded(value => !value)} aria-expanded={expanded} className="min-h-11 px-2 text-sm font-medium text-orange-700">{t(expanded ? 'training_field_compact' : 'training_field_expand')}</button>
      </div>
      {actors.length === 0 ? <p className="px-4 py-8 text-sm text-gray-500">{t('training_field_empty')}</p> : <ExerciseFieldPreview
        actors={actors}
        pitch={pitch}
        variant={variant}
        playbackState={previewState}
        className="w-full bg-emerald-50"
        style={{ height: expanded ? 'min(70vh, 680px)' : 'min(45vh, 400px)', minHeight: 240, maxHeight: 'none' }}
      />}

      {hasPlayback && <div className="relative z-10 -mt-px border-t border-gray-200 bg-white px-3 pb-3 pt-4 sm:px-5">
        <div className="mb-2 flex items-center justify-between gap-3 px-1 text-xs">
          <span className="min-w-0 truncate font-semibold text-gray-800">
            {stepLabel}
          </span>
          <span className="shrink-0 tabular-nums text-gray-500">{playbackTime}</span>
        </div>

        {progress}

        <div className="mt-2 grid min-h-[72px] grid-cols-[1fr_72px_1fr] items-center gap-2">
          <div className="flex items-center justify-center gap-2">
            <button type="button" onClick={reset} disabled={atStart} aria-label={t('training_playback_reset')} className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-700 disabled:opacity-35"><RotateCcw size={20} /></button>
            <button type="button" onClick={previous} disabled={atStart} aria-label={t('training_playback_previous')} className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-700 disabled:opacity-35"><ChevronLeft size={24} /></button>
          </div>

          <button
            type="button"
            onClick={isPreviewPlaying ? stop : play}
            aria-label={playLabel}
            className="flex h-[72px] w-[72px] -translate-y-1 items-center justify-center rounded-full border-4 border-white bg-primary-600 text-white shadow-lg"
          >
            {isPreviewPlaying ? <Pause size={30} fill="currentColor" /> : <Play size={32} fill="currentColor" />}
          </button>

          <div className="flex items-center justify-center gap-2">
            <span className="hidden min-w-11 text-center text-xs font-semibold tabular-nums text-gray-500 min-[430px]:inline">{currentStepIndex + 1}/{timeline.length}</span>
            <button type="button" onClick={next} disabled={atEnd} aria-label={t('training_playback_next')} className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-700 disabled:opacity-35"><ChevronRight size={24} /></button>
          </div>
        </div>
      </div>}
    </div>
  );
}
